import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { Header, SideLine, Footer, Background } from '$partials/global/index.js';
import { useAjaxListing } from '$hooks/ajax-listing';
import { AjaxListingContext, InfinitePagination } from '$partials/base/ajax-listing.jsx';
import { IconSearch, IconArrowRight, IconFilters, IconClose } from '$partials/icons/index.js';
import { MultiSelect } from '$partials/form/index.js';
import { useMultiSelectFilter } from '$hooks/filters';
import { CompanyItem } from '$partials/listings/index.js';
import { CompanyFilterDialog } from '$partials/filters/index.js';
import { getBackgroundColours, getSiteContext } from '$utilities/site-context';
import { DetailDialogProvider } from '$pages/detail/detail-page-provider';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const CompanyListingPage = (props) => {
    const page = props.data.page;

    // UI states
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);

    // Single filters
    const [termFilter, setTermFilter] = useState(page.items?.filters?.term?.value);

    // Multi filters
    const categoriesFilter = useMultiSelectFilter(page.items?.filters?.categories);
    const locationsFilter = useMultiSelectFilter(page.items?.filters?.locations);
    const regionsFilter = useMultiSelectFilter(page.items?.filters?.regions);
    const audiencesFilter = useMultiSelectFilter(page.items?.filters?.audiences);
    const coinvestorsFilter = useMultiSelectFilter(page.items?.filters?.coinvestors);
    const fundsFilter = useMultiSelectFilter(page.items?.filters?.funds);
    const companyStatusesFilter = useMultiSelectFilter(page.items?.filters?.companyStatuses);

    const listing = useAjaxListing({
        infinite: true,
        items: page.items?.edges,
        path: `/api/v1/listings/${encodeURIComponent(props.data.uri)}`,
        page: page?.items?.pageInfo?.page,
        totalItems: page.items?.total,
        totalPages: page.items?.totalPages,
        query: {
            'term': termFilter,
            'categories': categoriesFilter.value,
            'locations': locationsFilter.value,
            'regions': regionsFilter.value,
            'audiences': audiencesFilter.value,
            'coinvestors': coinvestorsFilter?.value,
            'funds': fundsFilter.value?.length > 0 ? fundsFilter.value : null,
            'companyStatuses': companyStatusesFilter.value,
            'spotlightSortKey': getSiteContext()?.branding
        }
    });

    const handleSubmit = (event) => {
        event.preventDefault();
        const formData = new FormData(event.target);
        setTermFilter(formData.get('term'));
    };

    const clearAllFilters = () => {
        categoriesFilter.setValue([]);
        locationsFilter.setValue([]);
        regionsFilter.setValue([]);
        audiencesFilter.setValue([]);
        fundsFilter.setValue([]);
        companyStatusesFilter.setValue([]);
    };

    const hasFilters = [
        ...categoriesFilter.value,
        ...locationsFilter.value,
        ...regionsFilter.value,
        ...audiencesFilter.value,
        ...coinvestorsFilter.value,
        ...fundsFilter.value,
        ...companyStatusesFilter.value,
    ].filter(Boolean)?.length > 0;

    return (
        <>
            <Background {...getBackgroundColours()}/>

            {/* Navigation */}
            <Header navigation={props.data.navigation}/>

            {/* Spacer */}
            <div className='pt-40 sm:pt-50 md:pt-80'></div>

            <main
                className='min-h-screen relative'
                id='main-content'>
                <SideLine/>

                <div className='layout-container'>
                    <div className='flex flex-wrap layout-negative-margin'>
                        {/* Heading */}
                        <div className='sm:w-9/12 layout-item-padding mb-60'>
                            <h2 className='type-style-2'>
                                {page.standfirst}
                            </h2>
                        </div>
                    </div>
                </div>

                {/* Filter row */}
                <div className='layout-container mb-60'>
                    <div className='flex flex-wrap layout-negative-margin'>
                        {/* Serch term filter */}
                        <div className='layout-item-padding w-full sm:w-6/12 md:w-4/12'>
                            <form
                                className='relative flex w-full bg-white rounded-full border border-white focus-within:border-black'
                                method='get'
                                onSubmit={handleSubmit}>
                                {/* Decorative icon */}
                                <div
                                    aria-hidden={true}
                                    className='absolute inset-y-0 left-0 flex items-center pl-12'>
                                    <IconSearch/>
                                </div>

                                {/* Field input */}
                                <label
                                    className='sr-only'
                                    htmlFor='term'>
                                    {'Search term'}
                                </label>
                                <input
                                    className='search-input block w-full bg-transparent py-5 pl-40 focus:outline-none'
                                    defaultValue={termFilter}
                                    id='term'
                                    name='term'
                                    placeholder='Search...'
                                    type='text' />

                                {/* Submit button */}
                                <button
                                    aria-label='Submit search'
                                    className='search-input-submit-button focus:outline-none pr-12 pl-5 arrow-button'
                                    type='submit'>
                                    <IconArrowRight/>
                                </button>
                            </form>
                        </div>

                        {/* Desktop filters */}
                        <div className='layout-item-padding md:w-8/12 hidden md:flex md:flex-wrap md:justify-start md:gap-10'>
                            {categoriesFilter?.options?.length > 0 && (
                                <MultiSelect
                                    label='Category'
                                    onChange={(v) => categoriesFilter.setValue(v)}
                                    options={categoriesFilter.options}
                                    value={categoriesFilter.value} />
                            )}
                            {(regionsFilter?.options?.length > 0 || locationsFilter?.options?.length > 0) && (
                                <MultiSelect
                                    label='Location'
                                    locationsFilter={locationsFilter}
                                    onChange={(v) => regionsFilter.setValue(v)}
                                    options={regionsFilter.options}
                                    value={regionsFilter.value} />
                            )}
                            {audiencesFilter?.options?.length > 0 && (
                                <MultiSelect
                                    label='Audience'
                                    onChange={(v) => audiencesFilter.setValue(v)}
                                    options={audiencesFilter.options}
                                    value={audiencesFilter.value} />
                            )}
                            {companyStatusesFilter?.options?.length > 0 && (
                                <MultiSelect
                                    label='Status'
                                    onChange={(v) => companyStatusesFilter.setValue(v)}
                                    options={companyStatusesFilter.options}
                                    value={companyStatusesFilter.value} />
                            )}
                            {fundsFilter?.options?.length > 0 && (
                                <MultiSelect
                                    label='Funds'
                                    onChange={(v) => fundsFilter.setValue(v)}
                                    options={fundsFilter.options}
                                    value={fundsFilter.value} />
                            )}
                        </div>

                        {/* xs/sm Open Filters */}
                        <div className='layout-item-padding mt-20 sm:mt-0 md:hidden'>
                            <button
                                className='type-style-7 bg-white py-8 px-20 rounded-full flex items-center'
                                onClick={setIsFiltersOpen.bind(null, true)}
                                type='button'>
                                {'Filters'}
                                <div
                                    aria-hidden='true'
                                    className='mt-1 pt-1 pl-5'>
                                    <IconFilters />
                                </div>
                            </button>

                            {isFiltersOpen && (
                                <CompanyFilterDialog
                                    audiencesFilter={audiencesFilter}
                                    categoriesFilter={categoriesFilter}
                                    clearAllFilters={clearAllFilters}
                                    companyStatusesFilter={companyStatusesFilter}
                                    fundsFilter={fundsFilter}
                                    hasFilters={hasFilters}
                                    isOpen={isFiltersOpen}
                                    locationsFilter={locationsFilter}
                                    onClose={setIsFiltersOpen.bind(null, false)}
                                    regionsFilter={regionsFilter} />
                            )}
                        </div>
                    </div>
                </div>

                {/* Number of results display */}
                {!hasFilters && (
                    <div className='layout-container-padding-right flex flex-wrap max-w-container'>
                        {/* Left Sidebar */}
                        <div className='w-sidebar'>
                            <div className='flex flex-col items-center'>
                            </div>
                        </div>
                        <div>
                            <p className='type-style-7'>
                                Showing {listing?.totalItems} companies
                            </p>
                        </div>
                    </div>
                )}

                {/* Selected filters row */}
                {hasFilters && (
                    <div className='layout-container'>
                        <div className='flex layout-negative-margin'>
                            {/* Serch term filter */}
                            <div className='layout-item-padding w-full inline-flex flex-wrap gap-10 items-center'>
                                <p className='type-style-7 whitespace-nowrap'>
                                    Showing {listing?.totalItems} companies with
                                </p>
                                <SelectedMultiFiltersItems filter={categoriesFilter}/>
                                <SelectedMultiFiltersItems filter={regionsFilter}/>
                                <SelectedMultiFiltersItems filter={locationsFilter}/>
                                <SelectedMultiFiltersItems filter={audiencesFilter}/>
                                <SelectedMultiFiltersItems filter={fundsFilter}/>
                                <SelectedMultiFiltersItems filter={companyStatusesFilter}/>
                                <SelectedMultiFiltersItems filter={coinvestorsFilter}/>

                                {/* Clear all */}
                                <button
                                    aria-label='Clear all filter'
                                    className='type-style-7 pl-5'
                                    onClick={clearAllFilters}
                                    type='button'>
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                <div className='layout-container-padding-right flex flex-wrap max-w-container mt-30 sm:pb-70 md:pb-100 rounded-md'>
                    {/* Left Sidebar */}
                    <div className='w-sidebar'>
                        <div className='flex flex-col items-center'>
                            <h2 className='type-style-8 align-middle rotate-180 writing-mode-vertical-rl'>
                                {page?.title}
                            </h2>
                        </div>
                    </div>

                    {/* Listing */}
                    <DetailDialogProvider isCompanyPage={true}>
                        <AjaxListingContext.Provider value={listing}>
                            <AjaxListingContext.Consumer>
                                {(listingState) => (
                                    <>
                                        <section
                                            aria-busy={listingState.loading}
                                            aria-label={page.title}
                                            className='flex-1 flex flex-wrap self-start'
                                            role='feed'>
                                            {listingState?.items?.length > 0 && (
                                                <>
                                                    <div className='w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 layout-gap gap-y-30 sm:gap-y-25 md:gap-y-30'>
                                                        {listingState.items.map(({ node: company }) => {
                                                            return (
                                                                <article
                                                                    className='-mr-20 sm:mr-0'
                                                                    key={company.id}>
                                                                    <CompanyItem
                                                                        {...company}
                                                                        openToModal={true}/>
                                                                </article>
                                                            );
                                                        })}
                                                    </div>

                                                    {/* Load more button */}
                                                    <InfinitePagination className='mt-20'>
                                                        {'Loading...'}
                                                    </InfinitePagination>
                                                </>
                                            )}
                                        </section>
                                    </>
                                )}
                            </AjaxListingContext.Consumer>
                        </AjaxListingContext.Provider>
                    </DetailDialogProvider>
                </div>
            </main>

            {/* Footer */}
            {props.data?.footer && (
                <Footer {...props.data.footer}/>
            )}
        </>
    );
};



/**
 * @param {PropTypes.InferProps<typeof selectedMultiFilterPropTypes>} props
 */
const SelectedMultiFiltersItems = ({ filter }) => {
    return filter.value?.length > 0 ?
        filter.value.map((value) => {
            const item = filter.options.find(({ value: option }) => option === value);
            return item ? (
                <button
                    aria-label={`Remove ${item.label} filter`}
                    className='type-style-7 self-start cursor-pointer select-none inline-flex items-center py-8 px-12 rounded-full border bg-black text-white border-black'
                    key={value}
                    onClick={filter.removeValue.bind(null, value)}
                    type='button'>
                    {item.label}
                    <div
                        aria-hidden={true}
                        className='-mb-1 pl-8'>
                        <IconClose fill='#ffffff' />
                    </div>
                </button>
            ) : null;
        })
        : null;
};


/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const selectedMultiFilterPropTypes = {
    filter: PropTypes.shape({
        removeValue: PropTypes.func.isRequired,
        value: PropTypes.array,
        options: PropTypes.array,
    })
};

SelectedMultiFiltersItems.propTypes = selectedMultiFilterPropTypes;

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.shape({
            __typename: PropTypes.string,
            title: PropTypes.string.isRequired,
            url: PropTypes.string,
            items: PropTypes.object,
            standfirst: PropTypes.string,
        }),
        uri: PropTypes.string,
        navigation: PropTypes.object,
        footer: PropTypes.object,
    }),
    domain: PropTypes.string,
};

CompanyListingPage.propTypes = propTypes;

export default CompanyListingPage;
